
import { Form, Flex, Row, Skeleton, Divider, Radio, RadioChangeEvent } from "antd";
import { useMemo, useState, useEffect } from "react";
import { ReactComponent as BoxIcon } from "../../../assets/images/BoxIcon.svg";
import { ReactComponent as SubBoxIcon } from "../../../assets/images/SubBoxIcon.svg";
import { ReactComponent as UserRound } from "../../../assets/images/UserRound.svg";
import { ReactComponent as PhoneOneIcon } from "../../../assets/images/phone1.svg";
import { ReactComponent as PhoneTwoIcon } from "../../../assets/images/phone2.svg";
import { ReactComponent as EnvelopeOpenIcon } from "../../../assets/images/envelopeOpen.svg";
import { ReactComponent as EnvelopeOpenOneIcon } from "../../../assets/images/envelopeOpen1.svg";
import { ReactComponent as EnvelopeOpenTwoIcon } from "../../../assets/images/envelopeOpen2.svg";
import { ReactComponent as PackageIcon } from "../../../assets/images/package.svg";
import { ReactComponent as SearchIcon } from "../../../assets/images/search.svg";
import { ReactComponent as FaceIcon } from "../../../assets/images/FaceIcon.svg";
import { ReactComponent as SailingIcon } from "../../../assets/images/sailing.svg";
import { ReactComponent as DistanceIcon } from "../../../assets/images/distance.svg";
import { ReactComponent as QuestionMark } from "../../../assets/images/question_mark.svg";
import dayjs from "dayjs";
import {
    CardWrapper,
    DisabledContent,
    FlexWrapper,
    IconWrapper,
    ImageContainer,
    ReportDetailsCart,
    ReportDetailsWrapper,
    StyledCol,
    StyledTitle,
    SubItemIcon,
    SubItemText,
    UpdatedText,
    StrikethroughText,
    ActivityDivider,
} from "./style";
import UpdateStickyModal from "../Modal/UpdateStickyModal";
import Images from "./ImageContent";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { useNavigate } from "react-router-dom";
import { Dimensions, FoundItemTypeOptions, FoundReport, FoundStatuses, UnitType, UnitTypeOptions } from "../../../types/FoundItemTypes";
import { updateNoteByAdminForFound } from "../../../redux/features/activityLog/activityLogAsyncThunk";
import { selectActivityLog } from "../../../redux/features/activityLog/activityLogSlice";
import { sortLostItemLogsData } from "../../../utils/helpers/sortStickyData";
import { convertUtcToLocalAndRelativeTime } from "../../../utils/helpers/convertUtcToLocal";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { LostItemActivityLogType, LostItemLogsData } from "../../../types/ActivityLog";
import AddComment from "./AddComment";
import AddUpdateCommentModal from "../Modal/AddUpdateCommentModal";
import { sortByDate } from "../../../utils/helpers/sortOptionsAlphabetically";
import { convertDimensionsUnits, convertToImperial, convertToMetric } from "../../ReportDetails/utils/formula";
import { applyUnitConversions } from "../../../utils/helpers/unitConversion";
import { ReactComponent as FaRuler } from "../../../assets/images/FaRuler.svg";
import { ReactComponent as FaRulerHorizontal } from "../../../assets/images/FaRulerHorizontal.svg";
import { ReactComponent as FaRulerVertical } from "../../../assets/images/FaRulerVertical.svg";
import { ReactComponent as FaWeightHanging } from "../../../assets/images/FaWeightHanging.svg";
import { ReactComponent as ContentIcon } from "../../../assets/images/content_paste_search.svg";
import { ActivationStatus } from "../../../types";
import ActivityLogs from "../../../components/ActivityLogs";


interface FoundReportDetailsProps {
    showUpdated: boolean;
    tabType: 'latest' | 'pending' | 'original' | 'version';
    activeData: FoundReport | null;
}

const dimensionsIcons: any = {
    Length: <FaRuler />,
    Width: <FaRulerHorizontal />,
    Height: <FaRulerVertical />,
    Weight: <FaWeightHanging />,
};


const FoundReportContents = ({ showUpdated, tabType, activeData }: FoundReportDetailsProps) => {
    const [form] = Form.useForm();
    const [commentForm] = Form.useForm();
    const [isMarkModalOpen, setIsMarkModalOpen] = useState(false);
    const [isModalOpenMarkAsUrgent, setIsModalOpenMarkAsUrgent] = useState(false);
    const [addUpdateCommentLoading, setAddUpdateCommentLoading] = useState(false);
    const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState<any | null>(null);
    const [measurementType, setMeasurementType] = useState<number>(1);
    const [dimensions, setDimensions] = useState<Dimensions>();
    const [productDimensions, setProductDimensions] = useState<any>({});

    const navigate = useNavigate()
    const dispatch = useAppDispatch();
    const { data: foundItemLogsData, status: foundItemLogStatus } = useAppSelector(selectActivityLog);
    const { status, versionData } = useAppSelector((state) => state.foundReport);
    const foundItemLogsDataSorted = useMemo(() => sortLostItemLogsData(foundItemLogsData), [foundItemLogsData]);

    // Use activeData instead of report for rendering content
    const dataToRender = useMemo(() => tabType === 'version' ? versionData : activeData, [versionData, activeData])

    const versionCount = dataToRender?.versionCount ?? 0;
    const hasLuggageItemType = dataToRender?.foundType ? Number(dataToRender?.foundType) === FoundItemTypeOptions.FoundLuggage : false;

    // Show edit buttons only in Latest tab or in Original tab when there's no other version
    const showEditButtons = tabType === 'latest' ||
        (tabType === 'original' && (dataToRender?.versionCount ?? 0) <= 1);

    const isDisabledContent = (tabType === "pending") || tabType === "version" || (versionCount > 1 && tabType === "original");

    const handleUnitChange = (e: RadioChangeEvent) => {
        if (!dimensions) return;


        // Use the updated conversion functions that don't perform automatic adjustments
        // const converted = e.target.value === UnitTypeOptions.Metric ? convertToMetric(dimensions) : convertToImperial(dimensions);
        const converted = convertDimensionsUnits(dimensions, measurementType as UnitType, e.target.value);
        if (!converted) return;

        const updatedDimensions = converted && applyUnitConversions(converted, e.target.value);

        if (!updatedDimensions) return;

        setProductDimensions(updatedDimensions);
        setDimensions(converted as Dimensions);
        setMeasurementType(e.target.value);
    };

    const renderUpdatedField = (currentValue: string, updatedValue?: string, showUpdated: boolean = false) => {
        if (updatedValue && currentValue !== updatedValue) {
            if (showUpdated) {
                return (
                    <>
                        <UpdatedText>{updatedValue}</UpdatedText>
                        {" "}
                        <StrikethroughText>{currentValue}</StrikethroughText>
                    </>
                );
            }
            return currentValue;
        }
        return currentValue;
    };

    const handleFormSubmit = (values: any) => {
        console.log("Form submitted with values:", values);
    };

    const handleCommentCancel = () => {
        setIsEditNoteModalOpen(false)
        setAddUpdateCommentLoading(false)
    };

    const handleUpdateCommentSubmit = async (values: any) => {
        try {
            setAddUpdateCommentLoading(true);
            if (editingRecord?.id) {
                await dispatch(updateNoteByAdminForFound({
                    foundId: editingRecord.foundId, note: values.note, id: editingRecord.id
                })).unwrap();
                handleCommentCancel();
            }
        } catch (error) {
            console.error("error:", error);
            setAddUpdateCommentLoading(false);
        }
    }

    const handleMarkChange = (e: any) => {
    };

    const handleMarkCancel = () => {
        setIsMarkModalOpen(false);
    };

    const handleEditNoteModalOpen = (type: "add" | "update", item: any) => {
        setEditingRecord(item);
        setIsEditNoteModalOpen(true);
    };

    const isLoading = useMemo(() => status === "loading", [status])

    useEffect(() => {
        if (dataToRender?.id) {
            setMeasurementType(dataToRender?.measurementType ? Number(dataToRender?.measurementType) : 1);

            const newDimensions: Dimensions = {
                length: {
                    main: dataToRender?.lengthPrimaryValue ?? 0,
                    sub: dataToRender?.lengthSecondaryValue ?? 0
                },
                width: {
                    main: dataToRender?.widthPrimaryValue ?? 0,
                    sub: dataToRender?.widthSecondaryValue ?? 0
                },
                height: {
                    main: dataToRender?.heightPrimaryValue ?? 0,
                    sub: dataToRender?.heightSecondaryValue ?? 0
                },
                weight: {
                    main: dataToRender?.weightPrimaryValue ?? 0,
                    sub: dataToRender?.weightSecondaryValue ?? 0
                }
            };
            const updatedDimensions = applyUnitConversions(newDimensions, dataToRender?.measurementType ?? 1);
            setProductDimensions(updatedDimensions);
            setDimensions(newDimensions);
        }
    }, [dataToRender]);

    useEffect(() => {
        if (editingRecord) {
            commentForm.setFieldsValue({ ...editingRecord, status: editingRecord.status === ActivationStatus.Active ? false : true })
        }
    }, [editingRecord])

    const isDiscardedStatus = useMemo(() => dataToRender?.status === FoundStatuses.Discarded, [dataToRender?.status])

    return (
        <DisabledContent isDeleting={isDisabledContent}>
            <Row gutter={12}>
                <StyledCol xs={24} sm={12} lg={12} xl={hasLuggageItemType ? 8 : 12}>
                    <ReportDetailsCart
                        title={`Found ${hasLuggageItemType ? "Luggage" : "Item"} Details`}
                    >
                        {isLoading ? (
                            <>
                                <Skeleton paragraph={{ rows: 6 }} active />
                                <Skeleton.Image style={{ width: 100, height: 100 }} />
                                <Skeleton.Image style={{ width: 100, height: 100 }} />
                                <Skeleton.Image style={{ width: 100, height: 100 }} />
                            </>
                        ) : (
                            <>
                                {dataToRender?.caseManager?.id && (
                                    <FlexWrapper>
                                        <SubItemIcon>
                                            <IconWrapper>
                                                <FaceIcon />
                                            </IconWrapper>
                                        </SubItemIcon>
                                        <SubItemText>Case Manager : </SubItemText>
                                        <SubItemText>{renderUpdatedField(dataToRender?.caseManager?.firstName + " " + dataToRender?.caseManager?.lastName, "", showUpdated)}</SubItemText>
                                    </FlexWrapper>
                                )}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <BoxIcon />
                                    </SubItemIcon>
                                    <SubItemText>Category : {renderUpdatedField(
                                        `${dataToRender?.category?.name || "Other Category"}`,
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>
                                {dataToRender?.subCategory?.id && (
                                    <FlexWrapper>
                                        <SubItemIcon>
                                            <IconWrapper>
                                                <SubBoxIcon />
                                            </IconWrapper>
                                        </SubItemIcon>
                                        <SubItemText>Sub Category :</SubItemText>
                                        <SubItemText>{renderUpdatedField(
                                            dataToRender?.subCategory?.name,
                                            "",
                                            showUpdated
                                        )}</SubItemText>
                                    </FlexWrapper>
                                )}

                                {sortByDate(dataToRender?.subCategory?.questions ?? [], "createdAt")?.map((question) => {
                                    switch (question.fieldType) {
                                        case 1:
                                            return (
                                                <FlexWrapper>
                                                    <SubItemIcon>
                                                        <IconWrapper>
                                                            <SubBoxIcon />
                                                        </IconWrapper>
                                                    </SubItemIcon>
                                                    <SubItemText>{question?.fieldName} :</SubItemText>
                                                    <SubItemText>{renderUpdatedField(
                                                        question?.answers?.answer === "true" ? "Yes" : question?.answers?.answer === "false" ? "No" : question?.answers === null ? "N/A" : question?.answers?.answer,
                                                        "",
                                                        showUpdated
                                                    )}</SubItemText>
                                                </FlexWrapper>
                                            );
                                        case 2:
                                            const selectedCheckBox: any = question?.options?.length > 0 ? question?.options?.find((option: any) => option?.name === question?.answers?.answer) : null;
                                            return (
                                                <>
                                                    <FlexWrapper>
                                                        <SubItemIcon>
                                                            <IconWrapper>
                                                                <SubBoxIcon />
                                                            </IconWrapper>
                                                        </SubItemIcon>
                                                        <SubItemText>{question?.fieldName} :</SubItemText>
                                                        <SubItemText>{renderUpdatedField(
                                                            question?.answers?.answer === "true" ? "Yes" : question?.answers?.answer === "false" ? "No" : question?.answers === null ? "No" : question?.answers?.answer,
                                                            "",
                                                            showUpdated
                                                        )}</SubItemText>
                                                    </FlexWrapper>
                                                    {selectedCheckBox?.subQuestion?.map((subQ: any) => {
                                                        return (
                                                            <FlexWrapper>
                                                                <SubItemIcon>
                                                                    <IconWrapper>
                                                                        <SubBoxIcon />
                                                                    </IconWrapper>
                                                                </SubItemIcon>
                                                                <SubItemText>{subQ?.fieldName} :</SubItemText>
                                                                <SubItemText>{renderUpdatedField(
                                                                    subQ?.answers?.answer,
                                                                    "",
                                                                    showUpdated
                                                                )}</SubItemText>
                                                            </FlexWrapper>
                                                        );
                                                    })}
                                                </>
                                            );
                                        case 3:
                                            const selectedOptionBrand: any = question?.options?.length > 0 ? question?.options?.find((option: any) => option?.name === question?.answers?.answer) : null;
                                            return (
                                                <>
                                                    <FlexWrapper>
                                                        <SubItemIcon>
                                                            <IconWrapper>
                                                                <SubBoxIcon />
                                                            </IconWrapper>
                                                        </SubItemIcon>
                                                        <SubItemText>{question?.fieldName} :</SubItemText>
                                                        <SubItemText>{renderUpdatedField(
                                                            question?.answers?.answer === "true" ? "Yes" : question?.answers?.answer === "false" ? "No" : question?.answers === null ? "N/A" : question?.answers?.answer,
                                                            "",
                                                            showUpdated
                                                        )}</SubItemText>
                                                    </FlexWrapper>
                                                    {selectedOptionBrand?.subQuestion?.length > 0 && sortByDate(selectedOptionBrand?.subQuestion ?? [], "createdAt")?.map((subQ: any) => {
                                                        return (
                                                            <FlexWrapper>
                                                                <SubItemIcon>
                                                                    <IconWrapper>
                                                                        <SubBoxIcon />
                                                                    </IconWrapper>
                                                                </SubItemIcon>
                                                                <SubItemText>{subQ?.fieldName} :</SubItemText>
                                                                <SubItemText>{renderUpdatedField(
                                                                    subQ?.answers?.answer,
                                                                    "",
                                                                    showUpdated
                                                                )}</SubItemText>
                                                            </FlexWrapper>
                                                        );
                                                    })}
                                                </>
                                            );
                                        default:
                                            return null;
                                    }
                                })}

                                <FlexWrapper>
                                    <SubItemIcon>
                                        <IconWrapper>
                                            <SubBoxIcon />
                                        </IconWrapper>
                                    </SubItemIcon>
                                    <SubItemText>Item Description :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.itemDescription ?? "",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>

                                <FlexWrapper>
                                    <SubItemIcon>
                                        <IconWrapper>
                                            <SubBoxIcon />
                                        </IconWrapper>
                                    </SubItemIcon>
                                    <SubItemText>Remarks :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.remarks ?? "",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>

                                <FlexWrapper>
                                    <SubItemIcon>
                                        <IconWrapper>
                                            <SubBoxIcon />
                                        </IconWrapper>
                                    </SubItemIcon>
                                    <SubItemText>Images:</SubItemText>
                                </FlexWrapper>
                                {dataToRender?.id && (
                                    <ImageContainer isActive={!isDisabledContent || !!dataToRender?.documents?.length}>
                                        {dataToRender?.documents?.length > 0 ? (
                                            <Images
                                                id={dataToRender.id}
                                                documents={dataToRender.documents}
                                                updatedDocuments={[]}
                                                showUpdated={showUpdated}
                                                showEditButtons={showEditButtons}
                                            />
                                        ) : (
                                            <p>No images found</p>
                                        )}
                                    </ImageContainer>
                                )}
                            </>
                        )}
                    </ReportDetailsCart>
                </StyledCol>
                <StyledCol xs={24} sm={12} lg={12} xl={hasLuggageItemType ? 8 : 12}>
                    <CardWrapper
                        title="Location Details"
                    >
                        {isLoading ? (
                            <Skeleton paragraph={{ rows: 6 }} active />
                        ) : (
                            <>
                                {/* Airport */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <SailingIcon />
                                    </SubItemIcon>
                                    <SubItemText>Airport :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.airport?.name ?? "",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>
                                {/* Location */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <DistanceIcon />
                                    </SubItemIcon>
                                    <SubItemText>Location :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.location?.name ?? "",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>
                                {sortByDate(dataToRender?.location?.questions ?? [], "createdAt")?.map((question) => {
                                    return (
                                        <FlexWrapper>
                                            <SubItemIcon style={{ width: 16, height: 16 }}>
                                                <QuestionMark />
                                            </SubItemIcon>
                                            <SubItemText>{question?.fieldName} :</SubItemText>
                                            <SubItemText>{question?.answers?.answer}</SubItemText>
                                        </FlexWrapper>
                                    );
                                })}
                                {/* Found Date */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <SearchIcon />
                                    </SubItemIcon>
                                    <SubItemText>Found Date :</SubItemText>
                                    <SubItemText>{renderUpdatedField(dataToRender?.foundDate ? dayjs(dataToRender.foundDate).format("MM/DD/YYYY") : "MM/DD/YYYY", "", showUpdated)}</SubItemText>
                                </FlexWrapper>
                                {/* Offload Date */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <PackageIcon />
                                    </SubItemIcon>
                                    <SubItemText>Offload Date :</SubItemText>
                                    <SubItemText>{dataToRender?.offloadDate ? renderUpdatedField(dataToRender?.offloadDate ? dayjs(dataToRender.offloadDate).format("MM/DD/YYYY") : "MM/DD/YYYY", "", showUpdated) : "N/A"}</SubItemText>
                                </FlexWrapper>
                            </>
                        )}
                    </CardWrapper>
                </StyledCol>

                {/* Passenger & Dimensions */}
                {hasLuggageItemType && <StyledCol xs={24} sm={12} lg={12} xl={hasLuggageItemType ? 8 : 12}>
                    <CardWrapper
                        title="Passenger & Dimensions"
                    >
                        {isLoading ? (
                            <Skeleton paragraph={{ rows: 6 }} active />
                        ) : (
                            <>
                                {/* Name */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <UserRound />
                                    </SubItemIcon>
                                    <SubItemText>Name :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.passengerName ?? "N/A",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>
                                {/* Additional Name */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <UserRound />
                                    </SubItemIcon>
                                    <SubItemText>Additional Name :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.passengerName2 ?? "N/A",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>

                                {/* Phone */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <PhoneOneIcon />
                                    </SubItemIcon>
                                    <SubItemText>Phone :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.passengerPhone ?? "N/A",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>
                                {/* Additional Phone */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <PhoneTwoIcon />
                                    </SubItemIcon>
                                    <SubItemText>Additional Phone :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.passengerPhone2 ?? "N/A",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>

                                {/* Email */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <EnvelopeOpenOneIcon />
                                    </SubItemIcon>
                                    <SubItemText>Email :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.passengerEmail ?? "N/A",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>
                                {/* Additional Email */}
                                <FlexWrapper>
                                    <SubItemIcon>
                                        <EnvelopeOpenTwoIcon />
                                    </SubItemIcon>
                                    <SubItemText>Additional Email :</SubItemText>
                                    <SubItemText>{renderUpdatedField(
                                        dataToRender?.passengerEmail2 ? dataToRender?.passengerEmail2 ?? "N/A" : "N/A",
                                        "",
                                        showUpdated
                                    )}</SubItemText>
                                </FlexWrapper>

                                <Divider />
                                <Radio.Group
                                    value={measurementType ?? 1}
                                    style={{ fontWeight: 600 }}
                                    options={[
                                        {
                                            label: "inches/lb",
                                            value: 1,
                                        },
                                        {
                                            label: "cm/kg",
                                            value: 2,
                                        },
                                    ]}
                                    size="large"
                                    onChange={handleUnitChange}
                                />
                            </>
                        )}

                        {/* Measurements */}
                        {Object.keys(productDimensions).map((key) => (
                            <FlexWrapper key={key}>
                                <SubItemIcon>
                                    {dimensionsIcons[key] || null}
                                </SubItemIcon>
                                <SubItemText>{key} :</SubItemText>
                                <SubItemText>{renderUpdatedField(
                                    productDimensions[key]?.main ?? "0",
                                    "",
                                    showUpdated
                                )} {renderUpdatedField(
                                    productDimensions[key]?.sub ?? "0",
                                    "",
                                    showUpdated
                                )}</SubItemText>
                            </FlexWrapper>
                        ))}
                        <FlexWrapper>
                            <SubItemIcon>
                                <ContentIcon />
                            </SubItemIcon>
                            <SubItemText>Contents :</SubItemText>
                            <SubItemText>{renderUpdatedField(
                                dataToRender?.description ? dataToRender?.description ?? "N/A" : "N/A",
                                "",
                                showUpdated
                            )}</SubItemText>
                        </FlexWrapper>
                    </CardWrapper>
                </StyledCol>}
            </Row>

            {/* Only show Activity section in Latest tab */}

            <ReportDetailsWrapper>
                <StyledTitle level={4}>Activity</StyledTitle>
                {isLoading ? (
                    <Skeleton paragraph={{ rows: 6 }} active />
                ) : (
                    <>
                        <ActivityDivider />
                        <ActivityLogs logs={foundItemLogsDataSorted} status={foundItemLogStatus} />
                        <AddComment foundId={dataToRender?.id?.toString()} />
                    </>
                )}
            </ReportDetailsWrapper>

            <UpdateStickyModal handleFormSubmit={handleFormSubmit} isMarkModalOpen={isMarkModalOpen} handleMarkCancel={handleMarkCancel} handleMarkChange={handleMarkChange} form={form} />

            {isEditNoteModalOpen && (
                <AddUpdateCommentModal
                    title="Update Comment"
                    confirmLabel="Update Comment"
                    handleFormSubmit={handleUpdateCommentSubmit}
                    isMarkModalOpen={isEditNoteModalOpen}
                    handleMarkCancel={handleCommentCancel}
                    form={commentForm}
                    loading={addUpdateCommentLoading}
                />
            )}
        </DisabledContent>
    )
};

export default FoundReportContents;
